/**
 * readinessBus.js — lightweight pub/sub for "the restaurant's setup state
 * has changed, anything watching `/api/onboarding/readiness` should
 * re-fetch now".
 *
 * Why this exists:
 *   - The Dashboard auto-saves restaurant settings without calling
 *     fetchMe() (for perf), so any component holding a one-shot
 *     `useEffect(() => fetch(readiness), [token])` would never see the
 *     updated checklist. Owners reported confused "❌ Phone Number Required"
 *     badges still showing after saving the phone.
 *
 * Pattern is intentionally tiny: one CustomEvent dispatched on window
 * + a `useReadinessRefresh` hook that subscribes to it AND to tab
 * visibility-change events (so returning to the tab triggers a refresh).
 */
import { useEffect } from 'react';

export const READINESS_CHANGED_EVENT = 'nofee:readiness-changed';

/** Call after ANY save that may have flipped a readiness signal. Cheap
 *  no-op when no listeners are attached.
 */
export const notifyReadinessChanged = () => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(READINESS_CHANGED_EVENT));
  } catch {
    /* IE11 fallback not required for this project */
  }
};

/** Subscribes the given async refetcher to:
 *    1. The READINESS_CHANGED_EVENT (fired explicitly after a save).
 *    2. document.visibilitychange — guarantees the checklist re-validates
 *       whenever the owner returns to the tab.
 *  The hook handles cleanup on unmount.
 */
export const useReadinessRefresh = (refetch) => {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof refetch !== 'function') return;
    const handler = () => { refetch(); };
    const visHandler = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    window.addEventListener(READINESS_CHANGED_EVENT, handler);
    document.addEventListener('visibilitychange', visHandler);
    return () => {
      window.removeEventListener(READINESS_CHANGED_EVENT, handler);
      document.removeEventListener('visibilitychange', visHandler);
    };
  }, [refetch]);
};
